import { useCallback, useRef } from 'react';
import type { KeyboardEvent, Ref } from 'react';

export interface RovingRadioOptions {
  /** Number of radios in the group. */
  count: number;
  /** Index of the checked radio, or -1 when none is. */
  selected: number;
  /** Called with the index the visitor chose (click, Enter, Space or an arrow key). */
  onSelect: (index: number) => void;
}

export interface RadioItemProps<T extends HTMLElement> {
  ref: Ref<T>;
  role: 'radio';
  'aria-checked': boolean;
  /** Only the checked radio (or the first, when none is) is in the tab order. */
  tabIndex: 0 | -1;
  onClick: () => void;
  onKeyDown: (event: KeyboardEvent<T>) => void;
}

/**
 * The WAI-ARIA radio-group keyboard model for a set of buttons: one tab stop; ← ↑ move to the previous
 * radio and → ↓ to the next (wrapping), Home / End jump to the ends — and moving checks the radio it
 * lands on, as radios do. Space and Enter check the focused one (a `button` already does).
 */
export function useRovingRadio<T extends HTMLElement>({
  count,
  selected,
  onSelect,
}: RovingRadioOptions): (index: number) => RadioItemProps<T> {
  const items = useRef<(T | null)[]>([]);
  const stop = selected >= 0 && selected < count ? selected : 0;

  return useCallback(
    (index: number): RadioItemProps<T> => ({
      ref: (element: T | null) => {
        items.current[index] = element;
        return () => {
          if (items.current[index] === element) items.current[index] = null;
        };
      },
      role: 'radio',
      'aria-checked': index === selected,
      tabIndex: index === stop ? 0 : -1,
      onClick: () => {
        onSelect(index);
      },
      onKeyDown: (event) => {
        let next: number;
        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            next = (index + 1) % count;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            next = (index - 1 + count) % count;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = count - 1;
            break;
          default:
            return;
        }
        event.preventDefault();
        onSelect(next);
        items.current[next]?.focus();
      },
    }),
    [count, selected, stop, onSelect],
  );
}
